<template>
  <section class="company-operations-panel">
    <div class="company-operations-panel__stack">
      <article class="company-operations-panel__card">
        <div class="company-operations-panel__card-head">
          <div>
            <p class="company-operations-panel__eyebrow">Operación</p>
            <h2 class="company-operations-panel__title">Direcciones</h2>
          </div>

          <button
            v-if="permissions.canCreate"
            class="company-operations-panel__primary-action"
            type="button"
            @click="$emit('create-address')"
          >
            Nueva dirección
          </button>
        </div>

        <div v-if="loading" class="company-operations-panel__empty">
          Cargando direcciones...
        </div>

        <div v-else-if="addresses.length" class="company-operations-panel__list">
          <article
            v-for="address in addresses"
            :key="address.id"
            class="company-operations-panel__item-card"
          >
            <div class="company-operations-panel__item-main">
              <div>
                <h3 class="company-operations-panel__item-title">
                  {{ address.name || 'Direccion sin nombre' }}
                </h3>
                <p class="company-operations-panel__item-line">
                  {{ address.address || 'Sin dirección' }}
                </p>
                <p class="company-operations-panel__item-meta">
                  {{ address.comuna_name || 'Sin comuna' }}
                </p>
              </div>
            </div>

            <div class="company-operations-panel__actions">
              <button
                v-if="permissions.canEdit"
                class="company-operations-panel__ghost-action"
                type="button"
                @click="$emit('edit-address', address)"
              >
                Editar
              </button>

              <button
                v-if="permissions.canDelete"
                class="company-operations-panel__danger-action"
                type="button"
                @click="$emit('delete-address', address)"
              >
                Eliminar
              </button>
            </div>
          </article>
        </div>

        <div v-else class="company-operations-panel__empty">
          No hay direcciones registradas.
        </div>
      </article>

      <div class="company-operations-panel__grid">
        <article class="company-operations-panel__card">
          <div class="company-operations-panel__card-head">
            <div>
              <p class="company-operations-panel__eyebrow">Operación</p>
              <h2 class="company-operations-panel__title">Contratos</h2>
            </div>

            <button
              v-if="permissions.canCreate"
              class="company-operations-panel__secondary-action"
              type="button"
              @click="$emit('create-contract')"
            >
              Nuevo contrato
            </button>
          </div>

          <div v-if="loading" class="company-operations-panel__empty">
            Cargando contratos...
          </div>

          <div v-else-if="contracts.length" class="company-operations-panel__contract-list">
            <article
              v-for="contract in contracts"
              :key="contract.id"
              class="company-operations-panel__contract-card"
            >
              <div class="company-operations-panel__contract-top">
                <div>
                  <h3 class="company-operations-panel__item-title">
                    {{ contract.concept || 'Contrato sin descripción' }}
                  </h3>
                  <p class="company-operations-panel__item-meta">
                    {{ contract.currency_description || 'Sin divisa' }}
                  </p>
                </div>

                <strong class="company-operations-panel__contract-value">
                  {{ formatMoney(contract.value, contract.currency_description) }}
                </strong>
              </div>

              <div class="company-operations-panel__actions company-operations-panel__actions--contract">
                <button
                  v-if="permissions.canEdit"
                  class="company-operations-panel__ghost-action"
                  type="button"
                  @click="$emit('edit-contract', contract)"
                >
                  Editar
                </button>

              <button
                v-if="permissions.canDelete"
                class="company-operations-panel__danger-action"
                type="button"
                @click="$emit('delete-contract', contract)"
                >
                  Eliminar
                </button>
              </div>
            </article>
          </div>

          <div v-else class="company-operations-panel__empty">
            No hay contratos registrados.
          </div>
        </article>

        <article class="company-operations-panel__card">
          <div class="company-operations-panel__card-head">
            <div>
              <p class="company-operations-panel__eyebrow">Tesoreria</p>
              <h2 class="company-operations-panel__title">Cuentas bancarias</h2>
            </div>

            <button
              v-if="permissions.canCreate"
              class="company-operations-panel__secondary-action"
              type="button"
              @click="$emit('create-bank-account')"
            >
              Nueva cuenta
            </button>
          </div>

          <div v-if="loading" class="company-operations-panel__empty">
            Cargando cuentas bancarias...
          </div>

          <div v-else-if="bankAccounts.length" class="company-operations-panel__contract-list">
            <article
              v-for="bankAccount in bankAccounts"
              :key="bankAccount.id"
              class="company-operations-panel__contract-card"
            >
              <div class="company-operations-panel__contract-top">
                <div>
                  <h3 class="company-operations-panel__item-title">
                    {{ bankAccount.bank_name || 'Cuenta sin banco' }}
                  </h3>
                  <p class="company-operations-panel__item-meta">
                    {{ bankAccount.account_type || 'Sin tipo' }} · {{ bankAccount.account_number || 'Sin numero' }}
                  </p>
                  <p class="company-operations-panel__item-line">
                    {{ bankAccount.account_holder || 'Sin titular' }}
                  </p>
                  <p v-if="bankAccount.payment_email" class="company-operations-panel__item-meta">
                    {{ bankAccount.payment_email }}
                  </p>
                </div>

                <strong v-if="bankAccount.is_primary" class="company-operations-panel__contract-value">
                  Principal
                </strong>
              </div>

              <div class="company-operations-panel__actions company-operations-panel__actions--contract">
                <button
                  v-if="permissions.canEdit"
                  class="company-operations-panel__ghost-action"
                  type="button"
                  @click="$emit('edit-bank-account', bankAccount)"
                >
                  Editar
                </button>

                <button
                  v-if="permissions.canDelete"
                  class="company-operations-panel__danger-action"
                  type="button"
                  @click="$emit('delete-bank-account', bankAccount)"
                >
                  Eliminar
                </button>
              </div>
            </article>
          </div>

          <div v-else class="company-operations-panel__empty">
            No hay cuentas bancarias registradas.
          </div>
        </article>

        <article class="company-operations-panel__card">
          <div class="company-operations-panel__card-head">
            <div>
              <p class="company-operations-panel__eyebrow">Integracion</p>
              <h2 class="company-operations-panel__title">Codigos internos</h2>
            </div>

            <button
              v-if="permissions.canEdit"
              class="company-operations-panel__secondary-action"
              type="button"
              @click="$emit('edit-codes')"
            >
              Editar ficha
            </button>
          </div>

          <p v-if="showLegalFiscalReferenceNotice" class="company-operations-panel__reference-copy">
            Los datos tributarios visibles en este bloque son referenciales y no participan en la ficha completa.
          </p>

          <div class="company-operations-panel__metrics" :class="{ 'company-operations-panel__metrics--reference': showLegalFiscalReferenceNotice }">
            <div class="company-operations-panel__metric">
              <span class="company-operations-panel__metric-label">Codigo REMU</span>
              <strong class="company-operations-panel__metric-value">
                {{ record.id_remu || 'Sin información' }}
              </strong>
            </div>

            <div class="company-operations-panel__metric">
              <span class="company-operations-panel__metric-label">Codigo CONTA</span>
              <strong class="company-operations-panel__metric-value">
                {{ record.id_conta || 'Sin información' }}
              </strong>
            </div>

            <div class="company-operations-panel__metric">
              <span class="company-operations-panel__metric-label">Facturador</span>
              <strong class="company-operations-panel__metric-value">
                {{ record.biller_type_name || 'Sin información' }}
              </strong>
            </div>

            <div class="company-operations-panel__metric">
              <span class="company-operations-panel__metric-label">Tipo organizacional</span>
              <strong class="company-operations-panel__metric-value">
                {{ record.organizational_type || 'Sin información' }}
              </strong>
            </div>
          </div>
        </article>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'CompanyOperationsPanel',
  props: {
    company: {
      type: Object,
      required: true
    },
    addresses: {
      type: Array,
      default: () => []
    },
    contracts: {
      type: Array,
      default: () => []
    },
    bankAccounts: {
      type: Array,
      default: () => []
    },
    record: {
      type: Object,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    permissions: {
      type: Object,
      required: true
    }
  },
  computed: {
    showLegalFiscalReferenceNotice () {
      return this.company && this.company.client_type === 'persona_natural' && this.record && this.record.requires_legal_fiscal_info === false
    }
  },
  methods: {
    formatMoney (value, currency) {
      if (value === undefined || value === null || value === '') return 'Sin monto'
      const formatted = Number(value).toLocaleString()
      return currency ? `${currency} ${formatted}` : formatted
    }
  }
}
</script>

<style scoped>
.company-operations-panel__stack {
  display: grid;
  gap: 1rem;
}

.company-operations-panel__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.company-operations-panel__card {
  padding: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.58);
  border-radius: 26px;
  background: linear-gradient(180deg, rgba(255, 252, 247, 0.98), rgba(246, 239, 231, 0.94));
  box-shadow: 0 20px 50px rgba(43, 34, 24, 0.06);
}

.company-operations-panel__card-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.9rem;
}

.company-operations-panel__eyebrow {
  margin: 0 0 0.35rem;
  font-size: 0.72rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(58, 46, 33, 0.48);
}

.company-operations-panel__title {
  margin: 0;
  color: #211913;
  font-size: 1.1rem;
}

.company-operations-panel__primary-action,
.company-operations-panel__secondary-action,
.company-operations-panel__ghost-action,
.company-operations-panel__danger-action {
  min-height: 2.4rem;
  padding: 0 0.85rem;
  border-radius: 999px;
}

.company-operations-panel__primary-action {
  border: 0;
  background: linear-gradient(135deg, #251d16 0%, #4c3c2d 100%);
  color: #f7f0e5;
}

.company-operations-panel__secondary-action,
.company-operations-panel__ghost-action {
  border: 1px solid rgba(102, 81, 57, 0.12);
  background: rgba(255, 255, 255, 0.66);
  color: #392d22;
}

.company-operations-panel__danger-action {
  border: 0;
  background: rgba(185, 92, 92, 0.14);
  color: #8a3f3f;
}

.company-operations-panel__list,
.company-operations-panel__contract-list {
  display: grid;
  gap: 0.75rem;
}

.company-operations-panel__item-card,
.company-operations-panel__contract-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.9rem;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.6);
}

.company-operations-panel__item-title {
  margin: 0;
  color: #241c16;
  font-size: 1rem;
}

.company-operations-panel__item-line {
  margin: 0.2rem 0 0;
  color: #4c3b2d;
}

.company-operations-panel__item-meta {
  margin: 0.3rem 0 0;
  color: rgba(45, 35, 26, 0.58);
  font-size: 0.86rem;
}

.company-operations-panel__contract-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
}

.company-operations-panel__contract-value {
  color: #7d5d25;
  white-space: nowrap;
}

.company-operations-panel__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  flex-shrink: 0;
}

.company-operations-panel__actions--contract {
  justify-content: flex-end;
}

.company-operations-panel__metrics {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.7rem;
}

.company-operations-panel__reference-copy {
  margin: 0 0 0.8rem;
  color: rgba(88, 67, 39, 0.76);
  font-size: 0.88rem;
  line-height: 1.5;
}

.company-operations-panel__metrics--reference .company-operations-panel__metric {
  background: rgba(255, 255, 255, 0.22);
  border: 1px dashed rgba(161, 125, 72, 0.28);
}

.company-operations-panel__metrics--reference .company-operations-panel__metric-value {
  color: rgba(36, 28, 22, 0.78);
}

.company-operations-panel__metric {
  padding: 0.8rem;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.6);
}

.company-operations-panel__metric-label {
  display: block;
  margin-bottom: 0.25rem;
  color: rgba(58, 46, 33, 0.52);
  font-size: 0.74rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.company-operations-panel__metric-value {
  color: #241c16;
  font-size: 1.05rem;
}

.company-operations-panel__empty {
  color: rgba(45, 35, 26, 0.58);
}

@media (max-width: 980px) {
  .company-operations-panel__grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .company-operations-panel__card-head,
  .company-operations-panel__item-card,
  .company-operations-panel__contract-card,
  .company-operations-panel__contract-top {
    flex-direction: column;
    align-items: flex-start;
  }

  .company-operations-panel__metrics {
    grid-template-columns: 1fr;
  }
}
</style>
